import React, { useState } from "react";
import chat from "../assets/chat.svg";
import propiedad1 from "../assets/aquilaLogo.svg";
import propiedad2 from "../assets/sample_logo.png";
import Spinner from "./Spinner";

function isImageUrl(url) {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url);
}

function toSnakeCase(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
}

function DataTable({
  columnsConfig,
  data,
  loading,
  showCheckboxes = false,
  onActionClick,
  onSortChange, // Nueva prop para manejar el ordenamiento desde el padre
  currentSort, // Estado actual del ordenamiento desde la API
  currentSearch,
  onSearchChange,
}) {
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Filtrar primero en toda la lista completa
  const filteredData = data.filter((row) =>
    columnsConfig.some((column) =>
      row[column.field]
        ?.toString()
        .toLowerCase()
        .includes(currentSearch.toLowerCase())
    )
  );

  // Luego aplicar la paginación sobre los datos ya filtrados
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSearchChange = (event) => {
    onSearchChange(event); // Mantiene la actualización del estado de búsqueda
    setCurrentPage(1); // Reinicia a la primera página al buscar
  };

  const handleSort = (key) => {
    let snakeKey = toSnakeCase(key);

    // Manejar el caso especial de fullName
    if (snakeKey === "full_name") {
      snakeKey = "first_name,last_name";
    }

    const isAscending = currentSort === snakeKey;
    const newSort = isAscending
      ? snakeKey
          .split(",")
          .map((k) => `-${k}`)
          .join(",") // Aplica `-` a cada clave
      : snakeKey;

    onSortChange(newSort);
  };

  return (
    <div className="inter-regular">
      {loading ? (
        <Spinner />
      ) : (
        <>
          <input
            type="text"
            placeholder="Buscar..."
            value={currentSearch}
            onChange={handleSearchChange}
            className="mb-4 p-2 border rounded w-full"
          />
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                {showCheckboxes && (
                  <th className="text-sm px-4 py-0.5 border-b border-r text-left text-[#D9D9D9]">
                    <input type="checkbox" className="mr-3" />
                  </th>
                )}
                {columnsConfig.map((column, index) => (
                  <th
                    key={index}
                    className={`font-semibold text-sm px-4 py-0.5 border-b text-left text-[#1F384C] border-r cursor-pointer `}
                    onClick={() => handleSort(column.field)}
                  >
                    {column.headerName}{" "}
                    {currentSort === column.field
                      ? "▲"
                      : currentSort === `-${column.field}`
                      ? "▼"
                      : ""}
                  </th>
                ))}
                <th className="text-sm px-6 py-3 border-b-[0.5px] text-left text-[#1F384C]">
                  Acción
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  row={row}
                  columnsConfig={columnsConfig}
                  showCheckboxes={showCheckboxes}
                  onActionClick={onActionClick}
                />
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center mt-4">
            {currentPage > 1 && (
              <button
                className="px-4 py-2 bg-[#F3F3F3] rounded border text-[#1F384C] font-semibold"
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Anterior
              </button>
            )}

            <span className="text-[#1F384C] font-semibold">
              Página {currentPage} de {totalPages}
            </span>

            {currentPage < totalPages && (
              <button
                className="px-4 py-2 bg-[#F3F3F3] rounded border text-[#1F384C] font-semibold"
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Siguiente
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TableRow({ row, columnsConfig, showCheckboxes, onActionClick }) {
  const [showPopup, setShowPopup] = useState(false);

  const handleActionClick = () => {
    setShowPopup((prev) => !prev);
  };

  const handleDetailsClick = () => {
    setShowPopup(false);
    if (onActionClick) {
      onActionClick(row);
    }
  };

  // Renderiza el estado actual con color y círculo
  const renderEstadoActual = (estado) => {
    const estados = {
      "En mantenimiento": { color: "#8A8CD9" },
      "En uso": { color: "rgba(28, 28, 28, 0.4)" },
      Disponible: { color: "#4AA785" },
    };

    const estadoConfig = estados[estado] || { color: "#D9D9D9" };

    return (
      <div className="flex items-center">
        <div
          className="w-3 h-3 rounded-full mr-2"
          style={{ backgroundColor: estadoConfig.color }}
        ></div>
        <span style={{ color: estadoConfig.color }}>{estado}</span>
      </div>
    );
  };

  // Renderiza el campo de propiedad con la imagen correspondiente
  const renderPropiedad = (propiedad) => {
    const imagenes = {
      Aquila: propiedad1,
      Sixt: propiedad2,
    };

    const imgSrc = imagenes[propiedad];
    return imgSrc ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "50%",
          border: "1px solid #E9E9E9",
          width: 28,
          height: 28,
        }}
      >
        <img
          src={imgSrc}
          alt={`Propiedad ${propiedad}`}
          style={{ objectFit: "cover", width: 12 }}
        />
      </div>
    ) : (
      <span>{propiedad || "N/A"}</span>
    );
  };

  return (
    <tr>
      {showCheckboxes && (
        <td className="flex items-center px-6 py-4 border-b-[0.5px] text-xs">
          <input type="checkbox" className="mr-3" />
        </td>
      )}
      {columnsConfig.map((column, index) => (
        <td
          key={index}
          className={`px-4 py-0.5 border-b-[0.5px] text-xs cursor-pointer ${
            index === 0 ? "max-w-[200px]" : ""
          }`}
          onClick={handleDetailsClick}
        >
          {column.field === "status" ? (
            renderEstadoActual(row[column.field])
          ) : column.field === "ownership" ? (
            renderPropiedad(row[column.field])
          ) : isImageUrl(row[column.field]) ? (
            <img
              src={row[column.field]}
              alt=""
              className="w-8 h-8 object-cover"
            />
          ) : column.field === "isCorporate" ? (
            row[column.field] ? (
              "Corporativo"
            ) : (
              "Particular"
            )
          ) : (
            row[column.field] || "N/A"
          )}
        </td>
      ))}
      <td className="w-10 px-2 py-4 border-b text-xs relative">
        <button
          onClick={handleActionClick}
          className="focus:outline-none flex space-x-1 items-center"
        >
          <span className="w-1 h-1 bg-[#D9D9D9] rounded-full"></span>
          <span className="w-1 h-1 bg-[#D9D9D9] rounded-full"></span>
          <span className="w-1 h-1 bg-[#D9D9D9] rounded-full"></span>
        </button>
        {showPopup && (
          <div
            style={{
              position: "absolute",
              left: "68%",
              transform: "translateX(-85%)",
              top: "100%",
              width: "200px",
              padding: "8px",
              borderRadius: "4px",
              backgroundColor: "white",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              zIndex: 10,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-6px",
                right: "33%",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderBottom: "6px solid white",
              }}
            ></div>
            <button
              onClick={handleDetailsClick}
              className="flex py-3 px-2 text-[#2C2C2C] text-[10px] hover:bg-[#F8F8F8] w-full"
            >
              <img src={chat} alt="Logo de detalles" className="mr-3" />
              Detalles
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default DataTable;
